/**
 * 视频通话挂断消息处理
 * 支持 28 种 end_reason 的分类处理
 */

import { useVideoCallStore } from '@/stores/videoCall'
import { useUserStore } from '@/stores/user'
import { closeVideoCall } from '@/components/VideoCallModal'
import { showToast } from 'vant'
import router from '@/router'
import agoraManager from '@/managers/AgoraRTCManager'
import { t } from '@/locales'
import { track } from '@/utils/tracker'
import {
  getHangupStrategy,
  END_REASON,
} from './errorHandler'

/**
 * 上传日志（RTC 问题排查）
 * @param {Object} message - 挂断消息
 * @param {string} endReason - 挂断原因
 */
async function uploadLogIfNeeded(message, endReason) {
  try {
    // TODO: 实现日志上传逻辑
    console.log('[hangUpHandler] Uploading log for RTC issue:', {
      endReason,
      liveId: message.msg_content?.live_id,
      timestamp: message.msg_content?.timestamp,
    })

    // 可以收集以下信息：
    // 1. Agora RTC 连接状态
    // 2. 网络质量数据
    // 3. 设备信息
    // 4. 频道加入时间
    // 5. 错误堆栈
  } catch (error) {
    console.error('[hangUpHandler] Failed to upload log:', error)
  }
}

/**
 * 显示充值引导（余额不足）
 * @param {string} hangUpMessage - 挂断提示消息
 */
function showRechargeGuide(hangUpMessage) {
  // TODO: 实现充值引导弹窗
  // 可以显示：
  // 1. 余额不足提示
  // 2. 充值按钮
  // 3. 套餐推荐

  // 临时使用 Toast
  showToast(hangUpMessage || t('common.insufficientBalance.message'))
}

/**
 * 显示对方繁忙提示
 * @param {string} hangUpMessage - 挂断提示消息
 */
function showBusyTips(hangUpMessage) {
  hangUpMessage && showToast(hangUpMessage)
}

/**
 * 清理 Agora 资源
 */
async function cleanupAgoraResources() {
  try {
    await agoraManager.destroy()
  } catch (error) {
    console.error('[hangUpHandler] Failed to cleanup Agora:', error)
  }
}

/**
 * 处理 UI 状态更新
 * @param {Object} videoCallStore - 视频通话 store
 * @param {string} currentRouteName - 当前路由名称
 */
function handleUIStateUpdate(videoCallStore, currentRouteName) {
  const isInVideoPage = currentRouteName === 'video'

  // 根据当前状态做不同处理
  if (videoCallStore.isCalling) {
    // 状态 1: 正在呼叫中（等待对方接听）
    closeVideoCall()
  } else if (videoCallStore.isConnected && isInVideoPage) {
    // 状态 2: 通话已连接且在视频页面
    router.back()
  }
}

/**
 * 判断当前用户是否为挂断动作的发起者
 * @param {Object} senderMember - 发起者信息对象
 * @returns {boolean} true=当前用户是发起者，false=当前用户是接收者
 */
function isCurrentUserSender(senderMember) {
  const userStore = useUserStore()
  const currentMemberId = userStore.getMemberId()
  const senderMemberId = senderMember?.member_id

  if (!currentMemberId || !senderMemberId) {
    return false
  }

  return String(currentMemberId) === String(senderMemberId)
}

/**
 * 判断当前用户是否应该跳过显示挂断提示
 * @param {boolean} isSender - 当前用户是否为 sender_member
 * @param {string} endReason - 挂断原因
 * @returns {boolean} true=跳过提示，false=显示提示
 */
function shouldSkipHangupToast(isSender, endReason) {
  // active_hang_up: 主动挂断，sender 是挂断发起者，不显示提示
  // sender_hang_up: 发起方挂断，如果当前用户是 sender，不显示提示
  if ([END_REASON.ACTIVE_HANG_UP, END_REASON.SENDER_HANG_UP].includes(endReason) && isSender) {
    return true
  }

  // receiver_hang_up: 接收方挂断，如果当前用户是 receiver（非 sender），不显示提示
  if (endReason === END_REASON.RECEIVER_HANG_UP && !isSender) {
    return true
  }

  // 其他情况都需要显示提示
  return false
}

/**
 * 处理不同类型的挂断
 * @param {Object} strategy - 挂断策略
 * @param {Object} message - 挂断消息
 * @param {string} hangUpMessage - 挂断提示消息
 */
async function handleByCategory(strategy, message, hangUpMessage) {
  const { needUploadLog, needShowRecharge, needShowBusyTips } = strategy
  const { end_reason: endReason, sender_member } = message.msg_content || {}
  const isSender = isCurrentUserSender(sender_member)

  // 1. RTC 问题：上传日志
  if (needUploadLog) {
    await uploadLogIfNeeded(message, endReason)
  }

  // 2. 判断是否需要跳过挂断提示（精确判断，排除自己挂断等情况，避免显示"你挂断了电话"）
  if (shouldSkipHangupToast(isSender, endReason)) {
    return
  }

  // 3. 余额不足：显示充值引导
  if (needShowRecharge) {
    // 延迟显示充值引导，确保页面切换完成
    setTimeout(() => {
      showRechargeGuide(hangUpMessage)
    }, 100)
    return // 已处理，不显示 Toast
  }

  // 4. 对方繁忙：显示特殊提示
  if (needShowBusyTips) {
    setTimeout(() => {
      showBusyTips(hangUpMessage)
    }, 100)
    return // 已处理，不显示 Toast
  }

  // 5. 正常挂断、超时、异常：显示普通 Toast
  setTimeout(() => hangUpMessage && showToast(hangUpMessage), 100)
}

/**
 * 处理视频通话挂断（包括服务端主动挂断，如钻石不足）
 * 场景：
 * 1. 自己发起->自己挂断时会下发
 * 2. 自己发起->服务端主动中断时会下发
 * @param {Object} event - EventBus 事件对象
 * @param {Object[]} event.data - IMManager 派发的消息数组
 */
export async function handleVideoCallHangUp(event) {
  console.log('[handleVideoCallHangUp] event:', event)
  const { data: messages } = event
  if (!Array.isArray(messages) || messages.length === 0) return

  const message = messages[0] || {}
  const videoCallStore = useVideoCallStore()

  // 如果已经是空闲状态，不处理
  if (videoCallStore.isIdle) {
    return
  }

  const { content_tips, end_reason: endReason, live_id } = message.msg_content || {}
  const currentRoomInfo = videoCallStore.currentRoomInfo

  // 如果当前不是通话中的房间，不处理
  // 如果 currentRoomInfo 为空，也不处理，自己挂断的时候已经把房间清空了
  if (!currentRoomInfo || String(live_id) !== String(currentRoomInfo.live_id)) {
    return
  }
  const currentRoute = router.currentRoute?.value

  // 获取挂断处理策略
  const strategy = getHangupStrategy(endReason)

  // 1. 上报挂断埋点
  const { end_reason } = message.msg_content || {}
  const isTrial = currentRoomInfo?.free_duration > 0

  // 使用 store 中的通话时长
  const duration = videoCallStore.callDuration

  track('call_1v1_status_change_video_room', {
    call_id: live_id,
    status: 'ended',
    duration,
    end_reason,
    is_trial: isTrial,
  })

  // 2. 处理 UI 状态更新（关闭弹窗/返回页面）
  handleUIStateUpdate(videoCallStore, currentRoute?.name)

  // 3. 重置通话状态
  videoCallStore.resetCall()

  // 4. 清理 Agora 资源（无论什么状态都要执行）
  await cleanupAgoraResources()

  // 5. 根据挂断类型执行不同处理（显示提示、上传日志、充值引导等）
  await handleByCategory(strategy, message, message.content_tips || content_tips)
}
